import { useComponentState } from "@faedev/dynamic-values";
import { composeClassNames } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { CSSProperties } from "react";
import { Alert, Button } from "react-bootstrap";
import Heading from "src/components/Heading";
import ModalLogin from "src/components/ModalLogin";
import ModalRegister from "src/components/ModalRegister";
import ModalResetPassword from "src/components/ModalResetPassword";
import Paragraph from "src/components/Paragraph";
import Translate from "src/components/Translate";
import constants from "src/constants";
import gutter from "src/functions/gutter";
import Benefit from "src/icons/Benefit";
import Calendar from "src/icons/Calendar";
import Community from "src/icons/Community";
import Filter from "src/icons/Filter";
import Free from "src/icons/Free";
import Watch from "src/icons/Watch";
import Yoga from "src/icons/Yoga";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sGroupTitle } from "src/styles/sGroupTitle.module.scss";
import { sWhyToUseCard } from "src/styles/sWhyToUseCard.module.scss";

type Props = {
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
  specificContent?: boolean;
};

function WhyToUse({ style, className, specificContent }: Props) {
  const { locale: lang, push } = useRouter();
  var openModalLogin = useComponentState(false);
  var openModalRegister = useComponentState(false);
  var openModalResetPassword = useComponentState(false);

  return (
    <>
      <div
        className={composeClassNames([
          sWhyToUseCard,
          className,
          "whyToUse-cards",
        ])}
        style={style}
      >
        <div className="whyToUse-card">
          <div className="whyToUse-card-image">
            <Free />
          </div>
          <div className="whyToUse-card-text">
            <Heading
              type="h4"
              align="left"
              title={<Translate tKey={`Free sample lectures`} />}
              style={{ fontSize: `17px` }}
              className={composeClassNames([
                sGroupTitle,
                "whyToUse-card-title",
              ])}
            />
            <p>
              <Translate
                tKey={`Before you buy, get to know the instructor thanks to sample lessons.`}
              />
            </p>
          </div>
        </div>
        <div className="whyToUse-card">
          <div className="whyToUse-card-image">
            <Benefit />
          </div>
          <div className="whyToUse-card-text">
            <Heading
              type="h4"
              align="left"
              title={<Translate tKey={`Benefit programs`} />}
              className={composeClassNames([
                sGroupTitle,
                "whyToUse-card-title",
              ])}
            />
            <p>
              <Translate
                tKey={`Get interesting discounts and bonuses from our partners.`}
              />
            </p>
          </div>
        </div>
        {!specificContent && (
          <div className="whyToUse-card">
            <div className="whyToUse-card-image">
              <Yoga />
            </div>
            <div className="whyToUse-card-text">
              <Heading
                type="h4"
                align="left"
                title={<Translate tKey={`Inspiring instructors`} />}
                className={composeClassNames([
                  sGroupTitle,
                  "whyToUse-card-title",
                ])}
              />
              <p>
                <Translate
                  tKey={`Follow the instructor who suits you and inspires you.`}
                />
              </p>
            </div>
          </div>
        )}
        {!specificContent && (
          <div className="whyToUse-card">
            <div className="whyToUse-card-image">
              <Watch />
            </div>
            <div className="whyToUse-card-text">
              <Heading
                type="h4"
                align="left"
                title={<Translate tKey={`Engaging content`} />}
                className={composeClassNames([
                  sGroupTitle,
                  "whyToUse-card-title",
                ])}
              />
              <p>
                <Translate
                  tKey={`Exercise according to your current needs or discover news.`}
                />
              </p>
            </div>
          </div>
        )}
        <div className="whyToUse-card">
          <div className="whyToUse-card-image">
            <Calendar />
          </div>
          <div className="whyToUse-card-text">
            <Heading
              type="h4"
              align="left"
              title={<Translate tKey={`Events and festivals`} />}
              className={composeClassNames([
                sGroupTitle,
                "whyToUse-card-title",
              ])}
            />
            <p>
              <Translate
                tKey={`Enjoy a festival, group event or individual session.`}
              />
            </p>
          </div>
        </div>
        <div className="whyToUse-card">
          <div className="whyToUse-card-image">
            <Filter />
          </div>
          <div className="whyToUse-card-text">
            <Heading
              type="h4"
              align="left"
              title={<Translate tKey={`Easy filtration`} />}
              className={composeClassNames([
                sGroupTitle,
                "whyToUse-card-title",
              ])}
            />
            <p>
              <Translate
                tKey={`Filter by video length, difficulty, style or focus.`}
              />
            </p>
          </div>
        </div>
        {!specificContent && (
          <div className="whyToUse-card">
            <div className="whyToUse-card-image">
              <Community />
            </div>
            <div className="whyToUse-card-text">
              <Heading
                type="h4"
                align="left"
                title={<Translate tKey={`Community support`} />}
                className={composeClassNames([
                  sGroupTitle,
                  "whyToUse-card-title",
                ])}
              />
              <p style={{ fontSize: `13px` }}>
                <Translate
                  tKey={`Did you buy the course? Share experiences or ask for advice.`}
                />
              </p>
            </div>
          </div>
        )}
      </div>
      <div className={"show-all"}>
        <Button
          onClick={() => push("#subscription")}
          className={composeClassNames([sButton])}
        >
          <Translate tKey={constants.Marketing.subscriptionMembersBtnTitle} />
        </Button>
      </div>

      {/* TODO: context provider for modal login */}
      <ModalLogin
        openModalLogin={openModalLogin}
        openModalRegister={openModalRegister}
        openModalResetPassword={openModalResetPassword}
        alert={
          <Alert variant={`warning`} className={sAlert}>
            <Paragraph
              width={`semibold`}
              align="center"
              style={gutter(`margin`, `all`, `0`)}
            >
              <Translate
                tKey={`You need to sing in to continue shopping. If you do not have an account, sign up first.`}
              />
              <Button
                variant="link"
                size="sm"
                className={sButton}
                onClick={() => {
                  openModalLogin.change(false);
                  openModalRegister.change(true);
                  openModalResetPassword.change(false);
                }}
              >
                <Translate tKey={`Sign up now!`} />
              </Button>
            </Paragraph>
          </Alert>
        }
        key={`ModalLogin`}
      />
      <ModalRegister
        openModalLogin={openModalLogin}
        openModalRegister={openModalRegister}
        key={`ModalRegister`}
      />
      <ModalResetPassword
        openModalResetPassword={openModalResetPassword}
        key={`ModalResetPassword`}
      />
    </>
  );
}

export default WhyToUse;
